import { AloftAreaJSON, AloftPrice } from '@aloft/shared-objects';
import {
  AloftArea,
  CraftsmanSFRQualityClasses,
  HasFeatureAreaAndQuality,
  Improvements,
  ManualAdjustments,
  ORDER_FEATURES,
  Site,
  Transaction,
  getFoundationDisplay,
  isDefined,
  isUndefined,
  manualAdjustmentKeys,
  orUndefined,
  schAloftPrice,
  syntaxFNMA,
  toPercentString,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { getFeatureQualityLabel } from '../../utils/getFeatureQualityLabel.ts';
import { CompAdjustments } from './CompAdjustments.ts';
import { Adjustments, selectAdjustments } from './adjustmentSelectors/selectAdjustments.ts';
import { applyRoundingAndThresholds } from './adjustmentSelectors/utils/applyRoundingAndThresholds.ts';
import { selectAdjustmentSettings, selectCompSequence } from './orderSelectors.ts';
import {
  selectImprovementsById,
  selectImprovementsEntities,
} from './propertyDataSelectors/improvementsSelectors.ts';
import { selectManualAdjustmentsEntities } from './propertyDataSelectors/manualAdjustmentsSelectors.ts';
import { selectSiteById, selectSiteEntities } from './propertyDataSelectors/siteSelectors.ts';
import {
  selectTransactionById,
  selectTransactionEntities,
} from './propertyDataSelectors/transactionSelectors.ts';

export const selectAdjustmentFeatures = createSelector(
  [selectImprovementsById, selectSiteById, selectTransactionById, (_state, id: string) => id],
  (improvements, site, transaction, id) => {
    if (isUndefined(site) || isUndefined(improvements) || isUndefined(transaction)) {
      return undefined;
    }

    const isSubject = id === 'subject';
    return getAdjustmentFeatures(site, improvements, transaction, isSubject);
  },
);

export const selectCompAdjustments = createSelector(
  [
    selectCompSequence,
    selectImprovementsEntities,
    selectSiteEntities,
    selectTransactionEntities,
    selectManualAdjustmentsEntities,
    selectAdjustments,
    selectAdjustmentSettings,
  ],
  (
    compSequence,
    improvementsEntities,
    siteEntities,
    transactionEntities,
    manualAdjustmentsEntities,
    allAdjustments,
    adjustmentSettings,
  ) => {
    if (
      isUndefined(compSequence) ||
      isUndefined(improvementsEntities) ||
      isUndefined(siteEntities) ||
      isUndefined(transactionEntities) ||
      isUndefined(manualAdjustmentsEntities) ||
      isUndefined(allAdjustments) ||
      isUndefined(adjustmentSettings)
    ) {
      return {};
    }

    return Object.fromEntries(
      compSequence.map((compID) => {
        const improvements = improvementsEntities[compID];
        const site = siteEntities[compID];
        const transaction = transactionEntities[compID];
        const manualAdjustments = manualAdjustmentsEntities[compID];

        const compAdjustment = allAdjustments[compID];
        const features =
          isUndefined(improvements) || isUndefined(site) || isUndefined(transaction)
            ? undefined
            : getAdjustmentFeatures(site, improvements, transaction, false);

        const price = transaction?.price ? AloftPrice.schema.parse(transaction.price) : undefined;
        const adjustments = getAdjustments(
          adjustmentSettings,
          compAdjustment,
          manualAdjustments,
          price,
        );

        const featuresAndAdjustments: CompAdjustments = {
          dateOfSale: {
            value: features?.dateOfSale.value,
            adjustment: adjustments.dateOfSale.adjustment,
          },
          termsOfSale: {
            value: features?.termsOfSale.value,
            adjustment: adjustments.termsOfSale.adjustment,
          },
          concessions: {
            value: features?.concessions.value,
            adjustment: adjustments.concessions.adjustment,
          },
          site: {
            size: {
              value: features?.site.size.value,
              adjustment: adjustments.site.size.adjustment,
            },
            location: {
              value: features?.site.location.value,
            },
            view: {
              value: features?.site.view.value,
            },
          },
          quality: {
            value: features?.quality.value,
            adjustment: adjustments.quality.adjustment,
          },
          condition: {
            value: features?.condition.value,
            adjustment: adjustments.condition.adjustment,
          },
          mainDwelling: {
            gla: {
              value: features?.mainDwelling.gla.value,
              adjustment: adjustments.mainDwelling.gla.adjustment,
            },
            units: {
              value: features?.mainDwelling.units.value,
              adjustment: adjustments.numberOfUnits?.adjustment,
            },
          },
          ...(improvements?.propertyType === 'multiFamily'
            ? {
                numberOfUnits: {
                  value: features?.numberOfUnits?.value,
                  adjustment: adjustments.numberOfUnits?.adjustment,
                },
              }
            : {}),
          basement: {
            value: features?.basement.value,
            adjustment: adjustments.basement.adjustment,
          },
          bathAboveGrade: {
            value: features?.bathAboveGrade.value,
            adjustment: adjustments.bathAboveGrade.adjustment,
          },
          bathBelowGrade: {
            value: features?.bathBelowGrade.value,
            adjustment: adjustments.bathBelowGrade.adjustment,
          },
          bedrooms: {
            adjustment: adjustments.bedrooms.adjustment,
          },
          garageCarport: {
            value: features?.garageCarport.value,
            adjustment: adjustments.garageCarport.adjustment,
          },
          porchPatioDeck: {
            value: features?.porchPatioDeck.value,
            adjustment: adjustments.porchPatioDeck.adjustment,
          },
          functionalUtility: adjustments.functionalUtility,
          externalObsolescence: adjustments.externalObsolescence,
          adu: {
            value: features?.adu.value,
            adjustment: adjustments.adu.adjustment,
          },
          barn: {
            value: features?.barn.value,
            adjustment: adjustments.barn.adjustment,
          },
          shed: {
            value: features?.shed.value,
            adjustment: adjustments.shed.adjustment,
          },
          workshop: {
            value: features?.workshop.value,
            adjustment: adjustments.workshop.adjustment,
          },
          pool: {
            value: features?.pool.value,
            adjustment: adjustments.pool.adjustment,
          },
          attic: {
            value: features?.attic.value,
            adjustment: adjustments.attic.adjustment,
          },
          fireplace: {
            value: features?.fireplace.value,
            adjustment: adjustments.fireplace.adjustment,
          },
          manual: adjustments.manual,

          netAdjustment: adjustments.netAdjustment,
          netAdjustmentPct: adjustments.netAdjustmentPct,
          grossAdjustmentPct: adjustments.grossAdjustmentPct,
          adjustedSalePrice: adjustments.adjustedSalePrice,
        };

        return [compID, featuresAndAdjustments] as [string, CompAdjustments];
      }),
    );
  },
);

const getAdjustmentFeatures = (
  site: Site,
  improvements: Improvements,
  transaction: Transaction,
  isSubject = false,
) => {
  const { area, location, view } = site;
  const {
    gla,
    uadQuality,
    craftsmanQuality,
    uadCondition,
    basement,
    fullBaths,
    halfBaths,
    garage,
    carport,
    detachedGarage,
    barn,
    shed,
    workshop,
    pool,
    spa,
    attic,
    fireplace,
    adu,
    effectiveAge,
    propertyType,
  } = improvements;
  const { concessions, termsOfSale } = transaction;
  const dateOfSale = isSubject ? undefined : formatDateOfSale(transaction);

  const foundationLabel =
    propertyType === 'manufacturedHome' ? getFoundationDisplay(improvements.foundation) : undefined;

  const numberOfUnits = propertyType === 'multiFamily' ? improvements.numberOfUnits : undefined;

  return {
    dateOfSale: toValue(dateOfSale),
    termsOfSale: toValue(termsOfSale),
    concessions: toValue(concessions),

    site: {
      size: toValue(orUndefined((val) => AloftArea.schema.parse(val).getSiteAreaString(''))(area)),
      location: toValue(location),
      view: toValue(view),
    },
    quality: toValue(`${getQuality(uadQuality, craftsmanQuality)}${foundationLabel ?? ''}`),
    condition: toValue(getCondition(uadCondition, effectiveAge)),
    mainDwelling: {
      gla: toValue(orUndefined((val) => AloftArea.schema.parse(val).getSiteAreaString(''))(gla)),
      units: toValue(numberOfUnits?.toString()),
    },
    numberOfUnits: toValue(numberOfUnits?.toString()),
    basement: toValue(getBasementFeature(basement)),
    bathAboveGrade: toValue(`${fullBaths ?? 0}.${halfBaths ?? 0}`),
    bathBelowGrade: toValue(`${basement?.fullBaths ?? 0}.${basement?.halfBaths ?? 0}`),
    garageCarport: toValue(getGarageCarportFeature(garage, carport, detachedGarage)),
    porchPatioDeck: toValue(getPorchPatioDeckFeature(improvements)),
    barn: toValue(getBarnShedWorkshopFeature('barn', barn)),
    shed: toValue(getBarnShedWorkshopFeature('shed', shed)),
    workshop: toValue(getBarnShedWorkshopFeature('workshop', workshop)),
    pool: toValue(getPoolSpaFeature(pool, spa)),
    attic: toValue(getAtticFeature(attic)),
    fireplace: toValue(getFireplaceFeature(fireplace)),
    adu: toValue(getADUFeature(adu)),
  };
};

const formatDateOfSale = ({ status, contractDate, saleDate }: Transaction): string | undefined => {
  if (!status) return undefined;

  return syntaxFNMA.dateOfSaleSyntax.encode({
    listingStatus: status,
    contractMonth: contractDate,
    closeMonth: saleDate,
  });
};

const mergeManualAdjustments = (
  adjustments: Adjustments,
  manualAdjustments?: ManualAdjustments,
): Adjustments => {
  if (!manualAdjustments) return adjustments;

  manualAdjustmentKeys.forEach((key) => {
    const manualAdjustmentOverride = manualAdjustments[key];
    if (isDefined(manualAdjustmentOverride)) {
      switch (key) {
        case 'manual':
          // eslint-disable-next-line no-param-reassign
          adjustments.manualCost = schAloftPrice.parse(manualAdjustments.manual);
          break;
        case 'units':
          // eslint-disable-next-line no-param-reassign
          adjustments.numberOfUnits = schAloftPrice.parse(manualAdjustmentOverride);
          break;
        default:
          // eslint-disable-next-line no-param-reassign
          adjustments[key] = schAloftPrice.parse(manualAdjustmentOverride);
          break;
      }
    }
  });

  return adjustments;
};

const getAdjustments = (
  adjustmentSettings: ReturnType<typeof selectAdjustmentSettings>,
  compAdjustments?: Adjustments,
  manualAdjustments?: ManualAdjustments,
  price?: AloftPrice,
) => {
  const {
    gla,
    numberOfUnits,
    condition,
    quality,
    site,
    bathAboveGrade,
    bathBelowGrade,
    bedrooms,
    garageCarport,
    porchPatioDeck,
    basement,
    poolSpa,
    shed,
    workshop,
    barn,
    attic,
    fireplace,
    manualCost,
    adu,
    functionalUtility,
    externalObsolescence,
    dateOfSale,
    termsOfSale,
    concessions,
  } = compAdjustments ?? {};

  const rowAdjustments: Adjustments = mergeManualAdjustments(
    {
      gla: applyRoundingAndThresholds(gla, 'mainDwelling', adjustmentSettings),
      numberOfUnits: applyRoundingAndThresholds(numberOfUnits, 'units', adjustmentSettings),
      condition: applyRoundingAndThresholds(condition, 'condition', adjustmentSettings),
      quality: applyRoundingAndThresholds(quality, 'quality', adjustmentSettings),
      site: applyRoundingAndThresholds(site, 'site', adjustmentSettings),
      shed: applyRoundingAndThresholds(shed, 'shed', adjustmentSettings),
      workshop: applyRoundingAndThresholds(workshop, 'workshop', adjustmentSettings),
      barn: applyRoundingAndThresholds(barn, 'barn', adjustmentSettings),
      bathAboveGrade: applyRoundingAndThresholds(
        bathAboveGrade,
        'bathAboveGrade',
        adjustmentSettings,
      ),
      bathBelowGrade: applyRoundingAndThresholds(
        bathBelowGrade,
        'bathBelowGrade',
        adjustmentSettings,
      ),
      basement: applyRoundingAndThresholds(basement, 'basement', adjustmentSettings),
      garageCarport: applyRoundingAndThresholds(garageCarport, 'garageCarport', adjustmentSettings),
      porchPatioDeck: applyRoundingAndThresholds(
        porchPatioDeck,
        'porchPatioDeck',
        adjustmentSettings,
      ),
      poolSpa: applyRoundingAndThresholds(poolSpa, 'pool', adjustmentSettings),
      attic: applyRoundingAndThresholds(attic, 'attic', adjustmentSettings),
      fireplace: applyRoundingAndThresholds(fireplace, 'fireplace', adjustmentSettings),
      manualCost: applyRoundingAndThresholds(manualCost, 'manual', adjustmentSettings),
      adu: applyRoundingAndThresholds(adu, 'adu', adjustmentSettings),
      functionalUtility: applyRoundingAndThresholds(
        functionalUtility,
        'functionalUtility',
        adjustmentSettings,
      ),
      externalObsolescence: applyRoundingAndThresholds(
        externalObsolescence,
        'externalObsolescence',
        adjustmentSettings,
      ),
      dateOfSale: applyRoundingAndThresholds(dateOfSale, 'dateOfSale', adjustmentSettings),
      bedrooms,
      termsOfSale,
      concessions,
    },
    manualAdjustments,
  );

  const netAdjustment = Object.values(rowAdjustments).reduce(
    (acc: AloftPrice, curr) => acc.add(curr),
    AloftPrice.zero,
  );

  const grossAdjustment = Object.values(rowAdjustments).reduce(
    (acc: AloftPrice, curr) => acc.add(curr ? curr.abs() : AloftPrice.zero),
    AloftPrice.zero,
  );

  const netAdjustmentPct = netAdjustment?.ratio(price);
  const grossAdjustmentPct = grossAdjustment?.ratio(price);
  const adjustedSalePrice = AloftPrice.add(price, netAdjustment);

  return {
    mainDwelling: {
      gla: toAdjustment(rowAdjustments.gla),
    },
    numberOfUnits: toAdjustment(rowAdjustments.numberOfUnits),
    bathAboveGrade: toAdjustment(rowAdjustments.bathAboveGrade),
    basement: toAdjustment(rowAdjustments.basement),
    bathBelowGrade: toAdjustment(rowAdjustments.bathBelowGrade),
    bedrooms: toAdjustment(rowAdjustments.bedrooms),
    garageCarport: toAdjustment(rowAdjustments.garageCarport),

    condition: toAdjustment(rowAdjustments.condition),
    quality: toAdjustment(rowAdjustments.quality),
    site: {
      size: toAdjustment(rowAdjustments.site),
    },

    porchPatioDeck: toAdjustment(rowAdjustments.porchPatioDeck),
    pool: toAdjustment(rowAdjustments.poolSpa),
    barn: toAdjustment(rowAdjustments.barn),
    shed: toAdjustment(rowAdjustments.shed),
    workshop: toAdjustment(rowAdjustments.workshop),
    attic: toAdjustment(rowAdjustments.attic),
    fireplace: toAdjustment(rowAdjustments.fireplace),
    manual: toAdjustment(rowAdjustments.manualCost),
    adu: toAdjustment(rowAdjustments.adu),
    functionalUtility: toAdjustment(rowAdjustments.functionalUtility),
    externalObsolescence: toAdjustment(rowAdjustments.externalObsolescence),

    dateOfSale: toAdjustment(rowAdjustments.dateOfSale),
    termsOfSale: toAdjustment(rowAdjustments.termsOfSale),
    concessions: toAdjustment(rowAdjustments.concessions),

    netAdjustment: {
      ...toValue(netAdjustment?.toString(true)),
      ...toAdjustment(netAdjustment),
    },
    netAdjustmentPct: toValue(netAdjustmentPct ? toPercentString(netAdjustmentPct) : '-'),
    grossAdjustmentPct: toValue(grossAdjustmentPct ? toPercentString(grossAdjustmentPct) : '-'),
    adjustedSalePrice: {
      ...toValue(adjustedSalePrice.toString(true)),
      ...toAdjustment(adjustedSalePrice),
    },
  };
};

const getQuality = (uadQuality?: number, craftsmanQuality?: CraftsmanSFRQualityClasses): string => {
  const uadQualityString = orUndefined((val?: number) => `Q${val}`)(uadQuality);
  const craftsmanQualityString = orUndefined((val?: number) => `${val}`)(craftsmanQuality);
  return [uadQualityString, craftsmanQualityString].join('/');
};

const getCondition = (uadCondition?: number, effectiveAge?: number): string => {
  const uadConditionString = orUndefined((val?: number) => `C${val}`)(uadCondition);
  const effectiveAgeString = orUndefined((val?: number) => `E${val}`)(effectiveAge);
  return [uadConditionString, effectiveAgeString].join('/');
};

const getBasementFeature = (basement?: {
  finishedArea?: AloftAreaJSON;
  totalArea?: AloftAreaJSON;
}): string => {
  const finishedArea = orUndefined((val) => AloftArea.schema.parse(val)?.getSiteAreaString(''))(
    basement?.finishedArea,
  );
  const totalArea = orUndefined((val) => AloftArea.schema.parse(val)?.getSiteAreaString(''))(
    basement?.totalArea,
  );

  return [totalArea, finishedArea].join('');
};

const getGarageCarportFeature = (
  garage?: { area?: AloftAreaJSON, hasFeature?: boolean },
  carport?: { area?: AloftAreaJSON, hasFeature?: boolean },
  detachedGarage?: { area?: AloftAreaJSON, hasFeature?: boolean },
) => {
  if (isUndefined(garage) && isUndefined(carport) && isUndefined(detachedGarage)) return '';

  const garageString = orUndefined(
    (val) => `${AloftArea.schema.parse(val).getSiteAreaString('')} AG`,
  )(garage?.hasFeature ? garage?.area : undefined);
  const carportString = orUndefined(
    (val) => `${AloftArea.schema.parse(val).getSiteAreaString('')} Carport`,
  )(carport?.hasFeature ? carport?.area : undefined);
  const detachedGarageString = orUndefined(
    (val) => `${AloftArea.schema.parse(val).getSiteAreaString('')} DG`,
  )(detachedGarage?.hasFeature ? detachedGarage?.area : undefined);

  return [garageString, carportString, detachedGarageString].filter(isDefined).join(';');
};

const getPorchPatioDeckFeature = (improvements: Improvements) => {
  const result: string[] = [];
  if (ORDER_FEATURES.porch.hasFeature(improvements)) result.push('Porch');
  if (ORDER_FEATURES.patio.hasFeature(improvements)) result.push('Patio');
  if (ORDER_FEATURES.deck.hasFeature(improvements)) result.push('Deck');

  return result.join(';');
};

const getBarnShedWorkshopFeature = (
  type: 'barn' | 'shed' | 'workshop',
  structure?: HasFeatureAreaAndQuality,
) => {
  if (isUndefined(structure)) return '';

  const area = orUndefined((val) => AloftArea.schema.parse(val).getSiteAreaString(''))(
    structure.area,
  );

  const quality = getFeatureQualityLabel(type, structure.quality, true);

  return `${area}/${quality}`;
};

const getAtticFeature = (attic?: Improvements['attic']) => {
  if (isUndefined(attic)) return '';

  const area = orUndefined((val) => AloftArea.schema.parse(val).getSiteAreaString(''))(attic.area);

  return area;
};

const getFireplaceFeature = (fireplace?: Improvements['fireplace']) => {
  if (isUndefined(fireplace)) return '';

  return `${fireplace.type} (${fireplace.count})`;
};

const getPoolSpaFeature = (pool?: Improvements['pool'], spa?: Improvements['spa']) => {
  if (isUndefined(pool) && isUndefined(spa)) return '';

  const poolString = isDefined(pool) && pool.hasFeature && isDefined(pool.area)
    ? `${AloftArea.schema.parse(pool.area).getSiteAreaString('')} Pool/${getFeatureQualityLabel(
        'pool',
        pool.quality,
        true,
      )}`
    : undefined;

  const spaString = isDefined(spa) && spa.hasFeature && isDefined(spa.area)
    ? `${AloftArea.schema.parse(spa.area).getSiteAreaString('')} Spa/${getFeatureQualityLabel(
        'spa',
        spa.quality,
        true,
      )}`
    : undefined;

  return [poolString, spaString].filter(isDefined).join('; ');
};

const getADUFeature = (adu?: Improvements['adu']) => {
  if (isUndefined(adu)) return '';

  const area = orUndefined((val) => AloftArea.schema.parse(val).getSiteAreaString(''))(adu.area);

  return `${area}/QL${adu.quality}`;
};

const toValue = (value?: string): { value: string } => ({ value: value ?? '' });

const toAdjustment = (adjustment?: AloftPrice): { adjustment?: AloftPrice } => ({
  adjustment,
});
